/* stylelint-disable no-descending-specificity */
import styled, { css, keyframes } from 'styled-components';

import breakpoints, { under } from '@polyai/ui/styles/config/breakpoints';
import themeColors from '@polyai/ui/styles/config/colors';
import themeSpacing from '@polyai/ui/styles/config/spacing';
import text from '@polyai/ui/styles/config/text';

import PolyaiLogoSlate from 'assets/svg/companyLogos/polyai-logo-slate-rgb.svg';
import PolyaiLogoWhite from 'assets/svg/companyLogos/polyai-logo-white-rgb.svg';

export const RightPanel = styled.div`
  position: relative;

  overflow: hidden;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: flex-start;

  padding: 1.5rem;

  background: ${themeColors.background};

  @media ${under(breakpoints.sm)} {
    padding: ${themeSpacing.sm3} ${themeSpacing.sm2} 0;
  }
`;

export const ContentContainer = styled.div`
  scrollbar-width: none; /* Firefox */

  overflow-y: auto;
  display: flex;
  flex: 1 1 0;
  align-items: center;
  justify-content: center;

  width: 100%;
  max-width: 420px;
  min-height: 0;
  margin: 0 auto;
  padding-bottom: ${themeSpacing.sm3};

  -ms-overflow-style: none; /* IE and Edge */


  &::-webkit-scrollbar {
    display: none; /* Chrome, Safari, Opera */
  }

  @media ${under(960)} {
    align-items: flex-start; /* Start from top on smaller screens */
    padding-bottom: ${themeSpacing.sm2}; /* Add padding to account for fixed footer on small screens */
  }

  @media (height <= 860px) {
    align-items: flex-start; /* Start from top on shorter viewports to prevent button cutoff */
    padding-bottom: ${themeSpacing.md2};
  }
`;

export const MainContent = styled.div`
  display: grid;
  gap: ${themeSpacing.md3};
  justify-items: center;

  width: 100%;
  padding: 2rem 0;

  border-radius: ${themeSpacing.xs3};

  @media ${under(960)} {
    padding: ${themeSpacing.sm2} 0 ${themeSpacing.md2}; /* Add bottom padding to ensure button is accessible */
  }
`;

export const BackButton = styled.button`
  cursor: pointer;

  position: absolute;
  z-index: 10;
  top: ${themeSpacing.sm3};
  left: ${themeSpacing.sm3};

  display: flex;
  align-items: center;
  justify-content: center;

  width: 32px;
  height: 32px;

  background: ${themeColors.background};
  border: 1px solid ${themeColors.borderSubtle};
  border-radius: 1000px;

  transition: background-color 0.2s ease, border-color 0.2s ease,
    transform 0.2s ease, color 0.2s ease;

  &:hover {
    border-color: ${themeColors.borderStrong};
  }

  @media ${under(breakpoints.sm)} {
    right: ${themeSpacing.sm3};
    left: auto;
  }
`;

export const TitleSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${themeSpacing.xs3};
  align-items: flex-start;

  width: 100%;
  margin-bottom: ${themeSpacing.md3};
`;

export const MainTitle = styled.h1`
  margin: 0;

  font-size: ${text.size.header.large};
  font-weight: ${text.weight.semibold};
  line-height: 1.2;
  color: ${themeColors.textPrimary};
`;

export const Subtitle = styled.p`
  margin: 0;
  font-size: ${text.size.body.regular};
  line-height: 1.3;
  color: ${themeColors.textSecondary};
`;

export const ExampleQuestions = styled.ul`
  display: flex;
  flex-direction: column;
  gap: ${themeSpacing.xs3};

  width: 100%;
  margin: 0;
  padding: 0;
  padding-bottom: ${themeSpacing.sm1};

  list-style: none;

  li {
    position: relative;
    padding-left: ${themeSpacing.sm1};

    &::before {
      content: '•';
      position: absolute;
      left: 0;
      color: ${themeColors.textSecondary};
    }
  }
`;

export const QuestionText = styled.span`
  font-size: ${text.size.body.regular};
  line-height: 1.3;
  color: ${themeColors.textSecondary};
`;

export const ScenariosToggle = styled.button`
  cursor: pointer;

  display: flex;
  flex: 1;
  gap: ${themeSpacing.xs1};
  align-items: center;
  justify-content: center;

  height: 40px;
  padding: ${themeSpacing.xs2} ${themeSpacing.sm2};

  font-size: ${text.size.body.regular};
  font-weight: ${text.weight.semibold};
  line-height: 1.3;
  color: ${themeColors.textPrimary};

  background: ${themeColors.background};
  border: 1px solid ${themeColors.borderSubtle};
  border-radius: 1000px;

  transition: background-color 0.2s ease, border-color 0.2s ease,
    box-shadow 0.2s ease;

  svg {
    width: 20px;
    height: 20px;
  }

  &:hover {
    background: ${themeColors.backgroundHover};
    border-color: ${themeColors.borderStrong};
  }

  &:focus-visible {
    outline: none;
    box-shadow: 0 0 0 3px ${themeColors.borderFocus};
  }

`;

export const AnimationContainer = styled.div`
  position: relative;

  display: flex;
  align-items: center;
  justify-content: center;

  width: 161.611px;
  height: 160px;
`;

export const ButtonsContainer = styled.div`
  display: flex;
  gap: ${themeSpacing.xs2};
  align-items: stretch;

  width: 100%;
  max-width: 420px;
`;

export const CallButton = styled.button<{ $isActive?: boolean }>`
  cursor: pointer;

  position: relative;
  z-index: 1;

  display: flex;
  flex: 1;
  gap: ${themeSpacing.xs1};
  align-items: center;
  justify-content: center;

  height: 40px;
  padding: ${themeSpacing.xs2} ${themeSpacing.sm2};

  font-size: ${text.size.body.regular};
  font-weight: ${text.weight.semibold};
  line-height: 1.3;
  color: ${p => (p.$isActive ? themeColors.textPrimary : themeColors.background)};

  background: ${p =>
    p.$isActive ? themeColors.background : themeColors.textPrimary};
  border: 1px solid
    ${p => (p.$isActive ? themeColors.borderStrong : themeColors.textPrimary)};
  border-radius: 1000px;

  transition: background-color 0.2s ease, color 0.2s ease, transform 0.2s ease,
    border-color 0.2s ease;

  &:hover {
    background: ${p =>
      p.$isActive ? themeColors.backgroundHover : themeColors.textSecondary};
  }
`;

export const ScenariosPanel = styled.div<{
  $open: boolean;
  $bannerOffset: number;
}>`
  will-change: transform;

  position: fixed;
  z-index: 1000;
  top: ${({ $bannerOffset }) => $bannerOffset}px;
  bottom: 0;
  left: 0;
  transform: translateX(${p => (p.$open ? '0' : '-100%')});

  overflow: hidden;
  display: flex;
  flex-direction: column;

  width: 50vw;

  background: ${themeColors.background};
  border-right: 1px solid ${themeColors.borderSubtle};

  transition: transform 260ms ease;

  @media ${under(breakpoints.sm)} {
    width: 100vw;
    border-right: none;
  }
`;

export const ScenariosHeader = styled.div`
  position: sticky;
  z-index: 1;
  top: 0;

  display: flex;
  flex-direction: column;
  flex-shrink: 0;
  gap: ${themeSpacing.xs1};

  padding: ${themeSpacing.sm3};

  background: ${themeColors.background};
  border-bottom: 1px solid ${themeColors.borderSubtle};
`;

export const ScenariosTitleRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
`;

export const ScenariosTitle = styled.h3`
  margin: 0;

  font-size: ${text.size.header.regular};
  font-weight: ${text.weight.semibold};
  line-height: 1.2;
  color: ${themeColors.textPrimary};
`;

export const CloseButton = styled.button`
  cursor: pointer;

  display: flex;
  align-items: center;
  justify-content: center;

  width: 36px;
  height: 36px;

  background: transparent;
  border: 1px solid transparent;
  border-radius: 1000px;

  transition: background-color 0.2s ease, border-color 0.2s ease,
    transform 0.2s ease, color 0.2s ease;

  &:hover {
    background: ${themeColors.backgroundHover};
    border-color: ${themeColors.borderSubtle};
  }

  svg {
    width: 16px;
    height: 16px;
    color: ${themeColors.textPrimary};
  }
`;

export const ScenariosDescription = styled.p`
  max-width: 100%;
  margin: 0;

  font-size: ${text.size.body.small};
  line-height: 1.2;
  color: ${themeColors.textSecondary};
`;

export const ScenariosContent = styled.div`
  overflow-y: auto;
  display: flex;
  flex: 1;
  flex-direction: column;
  gap: ${themeSpacing.xs3};

  min-height: 0;
  padding: 1.5rem;

  background: ${themeColors.background};

  @media ${under(breakpoints.sm)} {
    overflow-y: auto; /* Enable scrolling on small screens */
  }
`;

export const ScenarioCard = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${themeSpacing.xs1};

  padding: ${themeSpacing.sm1};

  background: ${themeColors.backgroundPage};
  border-radius: ${themeSpacing.xs3};

  transition: transform 0.2s ease, box-shadow 0.2s ease,
    background-color 0.2s ease, border-color 0.2s ease;

  &:hover {
    transform: translateY(-1px);
  }
`;

export const ScenarioCardTitle = styled.h4`
  margin: 0;
  margin-bottom: ${themeSpacing.sm2};

  font-size: ${text.size.header.regular};
  font-weight: ${text.weight.semibold};
  line-height: 1.2;
  color: ${themeColors.textPrimary};
`;

export const ScenarioSection = styled.div`
  display: flex;
  flex-direction: column;
  margin-bottom: ${themeSpacing.sm1};
`;

export const ScenarioLabel = styled.span` 
  font-size: ${text.size.body.small};
  font-weight: ${text.weight.semibold};
  line-height: 1.2;
  color: ${themeColors.textSecondary};
  letter-spacing: 0.5px;
`;

export const ScenarioSayList = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${themeSpacing.xs2};

  margin: 0;
  padding: 0;
`;

export const ScenarioList = styled.div`
  display: grid;
  grid-template-columns: max-content 1fr;
  gap: 0;
  align-items: center;

  margin: 0;
  padding: 0;

  border-radius: ${themeSpacing.xs2};
`;

export const ScenarioItem = styled.div`
  display: contents;

  padding: ${themeSpacing.xs1};

  font-size: ${text.size.body.small};
  font-weight: ${text.weight.regular};
  line-height: 1.4;
  color: ${themeColors.textPrimary};

  border-radius: ${themeSpacing.xs1};
`;

export const ScenarioItemLabel = styled.span`
  flex-shrink: 0;

  min-width: fit-content;

  font-size: ${text.size.body.regular};
  font-weight: ${text.weight.regular};
  color: ${themeColors.textSecondary};
`;

export const ScenarioItemValue = styled.span`
  flex: 1;

  padding: ${themeSpacing.xs1} 0;

  font-size: ${text.size.body.regular};
  font-weight: ${text.weight.semibold};
  color: ${themeColors.textPrimary};

  border-radius: ${themeSpacing.xs1};
`;

export const ScenarioSayValue = styled(ScenarioItemValue)`
  font-family: inherit;
  font-size: ${text.size.body.regular};
  font-weight: ${text.weight.semibold};
  font-style: normal;

  border: none;
`;

export const ScenarioDetailsValue = styled(ScenarioItemValue)`
  padding-left: ${themeSpacing.xs3};
  font-feature-settings: 'tnum' 1;
  font-variant-numeric: tabular-nums;
`;

export const Logo = styled(PolyaiLogoWhite)`
  position: absolute;
  top: ${themeSpacing.sm3};
  left: ${themeSpacing.sm3};
  transform: translateZ(0);

  display: block;

  width: auto;
  height: ${themeSpacing.sm4};

  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
  text-rendering: geometricprecision;

  shape-rendering: geometricprecision;
`;

export const MobileLogo = styled(PolyaiLogoSlate)`
  position: absolute;
  top: ${themeSpacing.sm3};
  left: ${themeSpacing.sm3};
  transform: translateZ(0);

  display: none;

  width: auto;
  height: ${themeSpacing.sm4};

  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
  text-rendering: geometricprecision;

  shape-rendering: geometricprecision;

  @media ${under(breakpoints.sm)} {
    display: none; /* Hide on small screens to avoid overlap with progress slider */
  }
`;

export const AgentStudioSection = styled.div<{ $visible: boolean }>`
  position: absolute;
  z-index: 1200;
  right: 0;
  bottom: 0;
  left: 0;
  transform: translateY(${p => (p.$visible ? '0' : '100%')});

  display: flex;
  gap: ${themeSpacing.sm1};
  align-items: center;
  justify-content: flex-start; /* allow manual pushing of elements to the right */

  width: 100%;
  padding: ${themeSpacing.sm1} ${themeSpacing.sm3};

  opacity: ${p => (p.$visible ? 1 : 0)};
  background: ${themeColors.background};
  border-top: 1px solid ${themeColors.borderSubtle};

  transition: opacity 0.3s ease, transform 0.3s ease, background-color 0.3s ease,
    border-color 0.3s ease;

  @media ${under(breakpoints.sm)} {
    position: fixed;
    right: 0;
    bottom: 0;
    left: 0;
  }
`;

export const AgentStudioText = styled.div`
  font-size: ${text.size.body.regular};
  line-height: 1.2;
  color: ${themeColors.textSecondary};
`;

const newPulse = keyframes`
  0% {
    transform: scaley(0.8) scalex(0.9);
  }
  55% {
    transform: scaley(1.2) scalex(1.1);
  }
  100% {
    transform: scaley(1.6) scalex(1.2);
    opacity: 0;
    filter: blur(5px);
  }
`;

/* Pulse animation for the lime circle inside the button */
const pulse = keyframes`
  0%, 100% {
    opacity: 1;
    transform: scale(1);
  }
  50% {
    opacity: 0.7;
    transform: scale(0.95);
  }
`;

export const AgentStudioButton = styled.button<{ $visible?: boolean }>`
  cursor: pointer;

  position: relative;

  display: flex;
  gap: ${themeSpacing.xs2};
  align-items: center;

  margin-left: auto;
  padding: ${themeSpacing.xs2} ${themeSpacing.sm1};

  font-size: ${text.size.body.small};
  font-weight: ${text.weight.semibold};
  color: ${themeColors.textPrimary};

  background: ${themeColors.background};
  border: 1px solid ${p => (p.$visible ? 'rgb(217 238 80)' : themeColors.borderSubtle)};
  border-radius: 1000px;

  transition: background-color 0.2s ease, border-color 0.2s ease,
    color 0.2s ease, transform 0.2s ease;

  ${p =>
    p.$visible
      ? css`
          &::before {
            pointer-events: none;
            content: '';

            position: absolute;
            z-index: -1;
            top: 0;
            left: 0;

            width: 100%;
            height: 100%;

            background-color: rgb(217 238 80);
            filter: blur(5px);
            border-radius: 1000px;

            animation: ${newPulse} 3.5s ease-in-out infinite;
          }
        `
      : ''}

  &:hover {
    background: ${themeColors.backgroundHover};
    border-color: ${themeColors.borderStrong};
  }

  svg {
    width: 16px;
    height: 16px;
  }

  /* Pulse animation for the first path (lime circle) in the SVG */
  svg path:first-child {
    transform-origin: center;
    animation: ${pulse} 2s ease-in-out infinite;
  }

  @media (prefers-reduced-motion: reduce) {
    &::before {
      animation: none !important;
    }

    svg path:first-child {
      animation: none !important;
    }
  }
`;
